import { useCallback, useMemo, useRef } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import useFetch from 'shared/hooks/useFetch';
import { queryString } from 'shared/util/query';
import type { BaseSearchParams, Filters, ResultData } from '@/domain';
import { apiListAdImageWrapper } from '@/utils/urlPrepender';
import { SEARCH_TIMEOUT_ERROR_MESSAGE } from './searchConstants';

type ExactPriceSearchConfig = {
    url: string;
    fetchKey: string;
};

const buildExactPriceSearchConfig = (searchId: string, filters: Filters, reactiveApiUrl: string): ExactPriceSearchConfig => {
    const {
        destination,
        booking_from,
        booking_to,
        no_of_beds_from,
        lat_sw,
        lng_sw,
        lat_ne,
        lng_ne,
        country,
        city,
        ...exactPriceParams
    }: Filters = filters;
    const params: BaseSearchParams = exactPriceParams;
    const queryStr = queryString(params);

    return {
        url: `${reactiveApiUrl}/reactive/page/${searchId}${queryStr}`,
        fetchKey: `${searchId}${queryStr}`,
    };
};

const MAX_POLL_ATTEMPTS = 30;

export const useExactPriceSearch = (searchId: string, filters: Filters, isEnabled = true) => {
    const { reactiveApiExternalUrl } = useConfig();
    const pollCountRef = useRef(0);

    const { url, fetchKey } = useMemo(
        () => buildExactPriceSearchConfig(searchId, filters, reactiveApiExternalUrl),
        [searchId, filters, reactiveApiExternalUrl],
    );

    const refreshInterval = useCallback((data: ResultData | undefined) => {
        if (data?.completed) {
            pollCountRef.current = 0;
            return 0;
        }

        pollCountRef.current += 1;

        if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
            return 0;
        }

        return 1000;
    }, []);

    const { data: rawData, error } = useFetch<ResultData>({
        url,
        isEnabled: isEnabled && Boolean(fetchKey),
        options: {
            refreshInterval,
            dedupingInterval: 0,
            revalidateOnFocus: false,
        },
    });

    const isCompleted = rawData?.completed === true;
    const hasTimedOut = pollCountRef.current >= MAX_POLL_ATTEMPTS;
    const data = isCompleted && rawData ? apiListAdImageWrapper(rawData) : undefined;
    const loading = !rawData || (!isCompleted && !hasTimedOut);
    const timeoutError = hasTimedOut ? new Error(SEARCH_TIMEOUT_ERROR_MESSAGE) : undefined;

    return { data, error: error || timeoutError, loading };
};
